/**
 * The peck loop: a question → retrieve the most relevant nest pages locally →
 * send them + the question to `/pwa/api/ask` → an answer with `[[slug]]`
 * citations. Retrieval never leaves the device; only the final prompt does.
 *
 * A rune-backed transcript of turns, so the Ask screen just renders `peck.turns`.
 */
import { ApiError } from './api';
import { askNest, type AskMessage } from './ask';
import { sendBehavior, sendRating } from './feedback';
import { nest } from './nest.svelte';
import {
  ANSWER_SYSTEM_PROMPT,
  buildAnswerUserPrompt,
  PECK_ANSWER_MAX_TOKENS,
  DEFAULT_SEARCH_LIMIT,
} from '../shared/prompts.js';
import { newId } from './id';
import type { PageType } from '../shared/coop-types.js';

export type TurnStatus = 'retrieving' | 'answering' | 'done' | 'error';

export interface PeckTurn {
  id: string;
  question: string;
  status: TurnStatus;
  answer: string;
  /** Pages sent to the model as context, in rank order. */
  cited: { slug: string; type: PageType }[];
  error: string | null;
  callId: string | null;
  model: string | null;
  /** 1 = 👍, 0 = 👎, null = not rated. */
  rated: 0 | 1 | null;
}

class Peck {
  turns = $state<PeckTurn[]>([]);

  get busy(): boolean {
    return this.turns.some((t) => t.status === 'retrieving' || t.status === 'answering');
  }

  reset(): void {
    this.turns = [];
  }

  async ask(question: string): Promise<void> {
    const q = question.trim();
    if (!q || this.busy) return;

    const id = newId();
    this.#push({
      id,
      question: q,
      status: 'retrieving',
      answer: '',
      cited: [],
      error: null,
      callId: null,
      model: null,
      rated: null,
    });

    const pages = nest.retrieve(q, DEFAULT_SEARCH_LIMIT);
    if (!pages.length) {
      this.#patch(id, {
        status: 'done',
        answer:
          nest.count === 0
            ? 'Your nest is empty here — sync it from the Read tab first.'
            : "I couldn't find anything in your nest about that.",
      });
      return;
    }

    this.#patch(id, {
      status: 'answering',
      cited: pages.map((p) => ({ slug: p.slug, type: p.type })),
    });

    const messages: AskMessage[] = [
      { role: 'system', content: ANSWER_SYSTEM_PROMPT },
      {
        role: 'user',
        content: buildAnswerUserPrompt(
          q,
          pages.map((p) => ({ slug: p.slug, type: p.type, content: p.body })),
        ),
      },
    ];

    try {
      const res = await askNest({ messages, max_tokens: PECK_ANSWER_MAX_TOKENS });
      this.#patch(id, {
        status: 'done',
        answer: res.answer.trim() || '(no answer)',
        callId: res.callId,
        model: res.model,
      });
    } catch (err) {
      this.#patch(id, {
        status: 'error',
        error: err instanceof ApiError ? err.message : 'The answer call failed. Try again.',
      });
    }
  }

  /** Re-run a turn (drops the old one). Used by the error state. */
  async retry(id: string): Promise<void> {
    const turn = this.turns.find((t) => t.id === id);
    if (!turn || this.busy) return;
    this.turns = this.turns.filter((t) => t.id !== id);
    await this.ask(turn.question);
  }

  /** 👍 / 👎 on an answered turn — a rating signal against its call. */
  rate(id: string, score: 0 | 1): void {
    const turn = this.turns.find((t) => t.id === id);
    if (!turn || turn.rated === score) return;
    this.#patch(id, { rated: score });
    if (turn.callId) void sendRating(turn.callId, score).catch(() => {});
  }

  /** Discard this answer and ask again — logs a `regenerated` behaviour signal. */
  async regenerate(id: string): Promise<void> {
    const turn = this.turns.find((t) => t.id === id);
    if (!turn || this.busy) return;
    if (turn.callId) void sendBehavior(turn.callId, 'regenerated').catch(() => {});
    this.turns = this.turns.filter((t) => t.id !== id);
    await this.ask(turn.question);
  }

  #push(turn: PeckTurn): void {
    this.turns = [...this.turns, turn];
  }

  #patch(id: string, fields: Partial<PeckTurn>): void {
    this.turns = this.turns.map((t) => (t.id === id ? { ...t, ...fields } : t));
  }
}

export const peck = new Peck();
